<template>
    <div
        v-if="properties?.length > 0 || (currentFilters?.price?.min && currentFilters?.price?.max)"
        class="mb-4 lg:mb-8 lg:px-0"
        aria-labelledby="current-filters-heading">
        <h2
            id="current-filters-heading"
            class="font-Thunder text-indian-khaki-950 hidden text-2xl font-medium uppercase lg:block">
            {{ $snippets['headless.plp.currentFilters'] }}
        </h2>

        <!-- Mobile: horizontal scrollable pills -->
        <div class="active-filters-slider flex w-full gap-3 overflow-x-auto pb-2 lg:hidden" aria-live="polite">
            <div class="my-auto !w-max">
                <button
                    v-if="properties?.length > 0"
                    @click="clearAllFilters"
                    class="plp-clear-filters mr-2 w-max">
                    {{ $snippets['headless.plp.clearEverything'] || 'Alles wissen' }}
                </button>
            </div>

            <!-- Unified filter pills (all filter types) -->
            <template v-for="filter in filters" :key="filter.code">
                <template v-for="option in getAllFilterItems(filter)" :key="option.id">
                    <template v-if="isOptionSelected(option) && !isDefaultBrandOption(option)">
                        <div class="!w-max">
                            <button
                                @click="invokeFilter(option.id, $event)"
                                class="plp-active-filter w-max">
                                {{ option.translated.name }}
                                <XMarkIcon class="size-4 shrink-0 stroke-2" aria-label="Verwijder filter" />
                            </button>
                        </div>
                    </template>
                </template>
            </template>

            <!-- Price filter pill -->
            <div v-if="currentFilters?.price?.min && currentFilters?.price?.max" class="!w-max">
                <button
                    @click="clearPrice"
                    class="plp-active-filter w-max">
                    <span class="currency-symbol">&euro;</span>{{ currentFilters.price.min }} -
                    <span class="currency-symbol">&euro;</span>{{ currentFilters.price.max }}
                    <XMarkIcon class="size-4 shrink-0 stroke-2" aria-label="Verwijder filter" />
                </button>
            </div>
        </div>

        <!-- Desktop: vertical list -->
        <div class="hidden flex-col gap-2 lg:flex" aria-live="polite">
            <!-- Unified filter pills (all filter types) -->
            <template v-for="filter in filters" :key="filter.code">
                <template v-for="option in getAllFilterItems(filter)" :key="option.id">
                    <template v-if="isOptionSelected(option)">
                        <template v-if="isDefaultBrandOption(option)">
                            <div class="active-filter-default">
                                {{ option.translated.name }}
                                <span class="text-indian-khaki-600 text-xs font-normal">(standaard)</span>
                            </div>
                        </template>
                        <template v-else>
                            <button
                                @click="invokeFilter(option.id, $event)"
                                class="plp-active-filter w-fit justify-between lg:w-full"
                                :aria-label="`Remove ${option.translated.name} filter`">
                                {{ option.translated.name }}
                                <XMarkIcon class="size-4 shrink-0" aria-hidden="true" />
                            </button>
                        </template>
                    </template>
                </template>
            </template>

            <!-- Price filter -->
            <button
                v-if="currentFilters?.price?.min && currentFilters?.price?.max"
                @click="clearPrice"
                class="plp-active-filter w-fit justify-between lg:w-full"
                :aria-label="`Remove price range €${currentFilters?.price?.min} - €${currentFilters?.price?.max}`">
                <span class="currency-symbol">&euro;</span>{{ currentFilters?.price?.min }} -
                <span class="currency-symbol">&euro;</span>{{ currentFilters?.price?.max }}
                <XMarkIcon class="size-4 shrink-0" aria-hidden="true" />
            </button>

            <!-- Clear all filters button -->
            <button
                v-if="properties?.length > 0"
                @click="clearAllFilters"
                class="plp-clear-filters my-auto w-fit text-left"
                aria-label="Clear all filters">
                {{ $snippets['headless.plp.removeAll'] }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { XMarkIcon } from '@heroicons/vue/24/solid';
    import { getAllFilterItems, parsePropertyIds } from '~/utils/plp';

    const props = withDefaults(
        defineProps<{
            currentFilters: {
                properties: Record<string, any>;
                price: {
                    min?: number;
                    max?: number;
                };
            };
            filters: Array<{
                code: string;
                label?: string;
                options?: Array<{
                    id: string;
                    translated: {
                        name: string;
                    };
                    customFields?: Record<string, any>;
                }>;
                entities?: Array<{
                    id: string;
                    translated: {
                        name: string;
                    };
                    customFields?: Record<string, any>;
                }>;
            }>;
            isOpen: boolean;
        }>(),
        {
            isOpen: true,
        },
    );

    const router = useRouter();
    const route = useRoute();
    const manufacturerId = inject<string | undefined>('manufacturerId', undefined);

    // All properties in the URL (single source of truth)
    const selectedPropertyIds = computed(() => parsePropertyIds(route.query.properties));

    // Detect the default brand option on brand pages
    const isDefaultBrandOption = (option: any): boolean => {
        if (!manufacturerId) return false;
        return option.customFields?.nd_tweakwise_isselected === true && !selectedPropertyIds.value?.includes(option.id);
    };

    // Unified selection check — works for all filter types since everything goes through ?properties=
    const isOptionSelected = (option: any): boolean => {
        if (selectedPropertyIds.value.includes(option.id)) return true;
        if (isDefaultBrandOption(option)) return true;
        return false;
    };

    // Computed properties for active filters
    const properties = computed(() => selectedPropertyIds.value);

    // Event emitters
    const emit = defineEmits(['invokeFilter']);

    const invokeFilter = (id: string, event: Event) => {
        emit('invokeFilter', id);
    };

    const clearPrice = () => {
        const query = { ...route.query };
        delete query['min-price'];
        delete query['max-price'];

        router.push({
            path: route.path,
            query,
        });
    };

    const clearAllFilters = () => {
        const newQuery: any = {};
        router.push({
            path: route.path,
            query: newQuery,
        });
    };
</script>

<style scoped>
    @reference "tailwindcss";

    .active-filter-default {
        @apply flex flex-col rounded-[12px] border px-4 py-2.5 text-sm font-medium;
        @apply w-fit opacity-80 lg:w-full;
        border-color: var(--color-indian-khaki-400);
        background-color: var(--color-indian-khaki-100);
        color: var(--color-indian-khaki-800);
    }
</style>
